import {MapRef} from 'react-map-gl/mapbox'
import {bearing, bezierSpline, lineString, length, along} from '@turf/turf'
import {Position} from 'geojson'

interface PathAnimationOptions {
  duration?: number // seconds (default is 10)
  rotate?: boolean // rotate the camera based on path direction (default is true)
  update?: (state: {phase: number}) => null // function to update {phase} each frame
}

const SMOOTH = 0.1

function lerp(n1: number, n2: number) {
  return (1 - SMOOTH) * n1 + SMOOTH * n2
}

export function followPath(map: MapRef, path: number[][], options: PathAnimationOptions = {}): void {
  const {duration = 10, rotate = true, update = () => null} = options
  const units = 'meters'
  const line = bezierSpline(lineString(path), {resolution: 99})
  const distance = length(line, {units})
  let startTime: number | null = null
  let stepTime: number | null = null
  let lastPoint: Position | null = null
  let lastBearing: number | null = null

  function frame(frameTime: number) {
    if (!startTime) {
      startTime = frameTime
    }
    const elapsed = (frameTime - startTime) / 1000 // seconds
    if (!stepTime) {
      stepTime = elapsed
    }
    const phase = elapsed / duration
    update({phase})
    if (phase >= 1) {
      return
    }
    const zoom = map.getZoom()
    const travelled = phase * distance
    const point = along(line, travelled, {units}).geometry.coordinates
    if (!lastPoint) {
      lastPoint = point
    }
    // TODO: there is elevation jitter, update camera position instead?
    map.setCenter([point[0], point[1]])
    // do not update last bearing as much when zoomed out
    // to help avoid jitter, update camera bearing every frame but smoothed out between bearing updates
    let updateRotation = false
    if (zoom >= 15 && Math.ceil(1000 * phase) % 10 === 0) {
      updateRotation = true
    } else if (zoom >= 12 && Math.ceil(1000 * phase) % 50 === 0) {
      updateRotation = true
    } else if (Math.ceil(1000 * phase) % 100 === 0) {
      updateRotation = true
    }
    if (rotate) {
      const nextPoint = along(line, Math.min(travelled * (1 + stepTime / duration), distance), {units})
      // avoid negatives by adding 360 so that lerp() works:
      //   -179 is only 2 degrees away from 179, not 358
      const nextBearing = bearing(point, nextPoint) + 360
      if (!lastBearing) {
        lastBearing = nextBearing
      }
      if (updateRotation) {
        lastBearing = nextBearing
      }
      map.setBearing(lerp(lastBearing, nextBearing))
    }
    lastPoint = point
    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)
}
